import React, { useState } from "react";

const OhmCalculator = ({ getStyles, theme, t }) => {
  const [values, setValues] = useState({
    voltage: "",
    resistance: "",
    current: "",
    power: "",
    puffsPerDay: "200",
    puffDuration: "2",
    batteryCapacity: "3000",
  });
  const [activeField, setActiveField] = useState(null);
  const [error, setError] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("custom");

  const presets = {
    mtl_basic: {
      name: t("mtlBasicPreset"),
      voltage: "3.7",
      resistance: "1.8",
      description: t("mtlBasicDescription"),
      puffsPerDay: "500",
      puffDuration: "3",
      batteryCapacity: "800",
    },
    mtl_salt: {
      name: t("mtlSaltPreset"),
      voltage: "3.5",
      resistance: "1.2",
      description: t("mtlSaltDescription"),
      puffsPerDay: "400",
      puffDuration: "2",
      batteryCapacity: "800",
    },
    rdl_medium: {
      name: t("rdlMediumPreset"),
      voltage: "4.2",
      resistance: "0.8",
      description: t("rdlMediumDescription"),
      puffsPerDay: "180",
      puffDuration: "2.5",
      batteryCapacity: "3000",
    },
    dl_cloud: {
      name: t("dlCloudPreset"),
      voltage: "4.2",
      resistance: "0.2",
      description: t("dlCloudDescription"),
      puffsPerDay: "150",
      puffDuration: "3",
      batteryCapacity: "3000",
    },
    dl_flavor: {
      name: t("dlFlavorPreset"),
      voltage: "3.7",
      resistance: "0.4",
      description: t("dlFlavorDescription"),
      puffsPerDay: "160",
      puffDuration: "2.5",
      batteryCapacity: "3000",
    },
    custom: {
      name: t("customPreset"),
      voltage: "",
      resistance: "",
      description: t("customDescription"),
      puffsPerDay: "200",
      puffDuration: "2",
      batteryCapacity: "3000",
    },
  };

  const calculate = () => {
    try {
      const v = parseFloat(values.voltage);
      const r = parseFloat(values.resistance);
      const i = parseFloat(values.current);
      const p = parseFloat(values.power);
      let newValues = { ...values };

      if (v && r) {
        newValues.current = (v / r).toFixed(2);
        newValues.power = (v * (v / r)).toFixed(2);
      } else if (v && i) {
        newValues.resistance = (v / i).toFixed(2);
        newValues.power = (v * i).toFixed(2);
      } else if (v && p) {
        newValues.current = (p / v).toFixed(2);
        newValues.resistance = ((v * v) / p).toFixed(2);
      } else if (r && i) {
        newValues.voltage = (r * i).toFixed(2);
        newValues.power = (r * i * i).toFixed(2);
      } else if (r && p) {
        newValues.current = Math.sqrt(p / r).toFixed(2);
        newValues.voltage = Math.sqrt(p * r).toFixed(2);
      } else if (i && p) {
        newValues.voltage = (p / i).toFixed(2);
        newValues.resistance = (p / (i * i)).toFixed(2);
      }

      setValues(newValues);
      setError("");
    } catch (err) {
      setError(t("calculationError"));
    }
  };

  const calculateLiquidConsumption = () => {
    const power = parseFloat(values.power) || 0;
    const puffsPerDay = parseInt(values.puffsPerDay) || 0;
    const puffDuration = parseFloat(values.puffDuration) || 0;
    const consumption = (power * puffDuration * puffsPerDay) / 1000;
    return consumption.toFixed(1);
  };

  const calculateBatteryLife = () => {
    const batteryCapacity = parseFloat(values.batteryCapacity) || 3000;
    const current = parseFloat(values.current) || 0;
    const puffsPerDay = parseInt(values.puffsPerDay) || 200;
    const puffDuration = parseFloat(values.puffDuration) || 2;

    const currentInMa = current * 1000;
    const consumoPerPuff = (currentInMa * puffDuration) / 3600;
    const fattoreCorrezione = 0.7;

    const oreUtilizzo =
      (batteryCapacity / (consumoPerPuff * puffsPerDay * fattoreCorrezione)) *
      24;
    return Math.round(oreUtilizzo * 2) / 2;
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (preset === "custom") {
      setValues({
        ...values,
        voltage: "",
        resistance: "",
        current: "",
        power: "",
        puffsPerDay: "200",
        puffDuration: "2",
        batteryCapacity: "3000",
      });
      return;
    }

    const v = parseFloat(presets[preset].voltage);
    const r = parseFloat(presets[preset].resistance);
    const i = (v / r).toFixed(2);
    const p = (v * (v / r)).toFixed(2);

    setValues({
      ...values,
      voltage: presets[preset].voltage,
      resistance: presets[preset].resistance,
      current: i,
      power: p,
      puffsPerDay: presets[preset].puffsPerDay,
      puffDuration: presets[preset].puffDuration,
      batteryCapacity: presets[preset].batteryCapacity,
    });
  };

  const handleInput = (field, value) => {
    if (value === "" || (!isNaN(value) && value >= 0)) {
      setValues({ ...values, [field]: value });
    }
  };

  const clear = () => {
    setValues({
      voltage: "",
      resistance: "",
      current: "",
      power: "",
      puffsPerDay: "200",
      puffDuration: "2",
      batteryCapacity: "3000",
    });
    setError("");
    setActiveField(null);
    setSelectedPreset("");
  };

  return (
    <div style={getStyles(theme).content}>
      <div style={getStyles(theme).section}>
        <h2 style={getStyles(theme).sectionTitle}>{t("ohmCalculator")}</h2>

        <div style={{ marginBottom: "20px" }}>
          <p style={getStyles(theme).label}>{t("selectPreset")}</p>
          <select
            style={getStyles(theme).select}
            value={selectedPreset}
            onChange={(e) => handlePresetChange(e.target.value)}
          >
            {Object.entries(presets).map(([key, preset]) => (
              <option key={key} value={key}>
                {preset.name}
              </option>
            ))}
          </select>

          {selectedPreset && (
            <p
              style={{
                marginTop: "5px",
                marginBottom: "15px",
                fontSize: "13px",
                color: "var(--text)",
                opacity: "0.8", // Rende il testo leggermente più tenue
              }}
            >
              {presets[selectedPreset].description}
            </p>
          )}
        </div>

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("voltage")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={values.voltage}
              onChange={(e) => handleInput("voltage", e.target.value)}
              onFocus={() => setActiveField("voltage")}
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>V</span>
          </div>
        </div>

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("resistance")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={values.resistance}
              onChange={(e) => handleInput("resistance", e.target.value)}
              onFocus={() => setActiveField("resistance")}
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>Ω</span>
          </div>
        </div>

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("current")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={values.current}
              onChange={(e) => handleInput("current", e.target.value)}
              onFocus={() => setActiveField("current")}
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>A</span>
          </div>
        </div>

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("power")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={values.power}
              onChange={(e) => handleInput("power", e.target.value)}
              onFocus={() => setActiveField("power")}
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>W</span>
          </div>
        </div>

        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <h3 style={{ ...getStyles(theme).subSectionTitle }}>
            {t("consumptionEstimates")}
          </h3>

          <div style={getStyles(theme).inputGroup}>
            <label style={getStyles(theme).label}>{t("puffsPerDay")}</label>
            <div style={getStyles(theme).inputWrapper}>
              <input
                style={getStyles(theme).input}
                type="number"
                value={values.puffsPerDay}
                onChange={(e) => handleInput("puffsPerDay", e.target.value)}
                min="0"
              />
            </div>
          </div>

          <div style={getStyles(theme).inputGroup}>
            <label style={getStyles(theme).label}>{t("puffDuration")}</label>
            <div style={getStyles(theme).inputWrapper}>
              <input
                style={getStyles(theme).input}
                type="number"
                value={values.puffDuration}
                onChange={(e) => handleInput("puffDuration", e.target.value)}
                min="0"
                step="0.5"
              />
              <span style={getStyles(theme).unit}>{t("seconds")}</span>
            </div>
          </div>

          <div style={getStyles(theme).inputGroup}>
            <label style={getStyles(theme).label}>{t("batteryCapacity")}</label>
            <div style={getStyles(theme).inputWrapper}>
              <input
                style={getStyles(theme).input}
                type="number"
                value={values.batteryCapacity}
                onChange={(e) => handleInput("batteryCapacity", e.target.value)}
                min="0"
              />
              <span style={getStyles(theme).unit}>mAh</span>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "var(--accent)",
              color: "white",
              borderRadius: "8px",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <button style={getStyles(theme).button} onClick={calculate}>
            {t("calculate")}
          </button>
          <button
            style={{
              ...getStyles(theme).button,
              backgroundColor: "var(--inputBackground)",
              color: "var(--text)",
            }}
            onClick={clear}
          >
            {t("clear")}
          </button>
        </div>

        {(values.power || values.current) && (
          <div style={getStyles(theme).resultContainer}>
            <div style={getStyles(theme).resultItem}>
              <strong>{t("estimatedLiquidConsumption")}:</strong>{" "}
              {calculateLiquidConsumption()} ml/day
            </div>
            <div style={getStyles(theme).resultItem}>
              <strong>{t("estimatedBatteryLife")}:</strong>{" "}
              {calculateBatteryLife() < 24
                ? `${calculateBatteryLife()} ${t("hours")}`
                : `${(calculateBatteryLife() / 24).toFixed(1)} ${t("days")}`}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            backgroundColor: "var(--sectionBackground)",
            borderRadius: "8px",
          }}
        >
          <h3 style={getStyles(theme).subSectionTitle}>{t("vapingTips")}</h3>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
            <li style={{ marginBottom: "8px" }}>{t("mtlTip")}</li>
            <li style={{ marginBottom: "8px" }}>{t("rdlTip")}</li>
            <li style={{ marginBottom: "8px" }}>{t("dlTip")}</li>
            <li style={{ marginBottom: "8px" }}>{t("nicSaltTip")}</li>
            <li>{t("powerTip")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OhmCalculator;
